import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, X, Bell, Sun, Moon, ChevronDown, ChevronUp, Settings,
  AlertTriangle, Calendar, MessageSquare, Edit, ArrowUpRight, 
  FileText, Check, User, Briefcase, Clock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { mockProjects, mockUsers } from '../../mock/data';

// Mock escalation data combining projects and action items
const mockEscalations = [
  {
    id: 'esc-1',
    type: 'project',
    title: 'Financial Benefits of Oracle HCM Implementation (from 2023) 2024',
    assignee: 'Ahmed Safat',
    subAssignee: 'Hassan Ali',
    sourceBadge: 'BSC',
    summary: 'After aligning with Hassan Ali the new project owner will be Mr. Ahmed Sadeq, a project charter will be provided shortly.',
    updateDate: '23 Jul 2025',
    escalationDate: '20 Jul 2025',
    escalationReason: 'Delayed milestone - requires executive decision',
    escalatedTo: 'Chairman',
    escalatedBy: 'Mohamed Deyab',
    source: 'Board Meeting - Q3 Review',
    sourceType: 'meeting',
    tags: ['HR', 'Late'],
    refNumber: '758793',
    status: 'pending',
  },
  {
    id: 'esc-2',
    type: 'action-item',
    title: 'Review Q4 Financial Projections',
    assignee: 'Sarah Johnson',
    subAssignee: null,
    sourceBadge: 'Finance',
    summary: 'Quarterly financial review pending CFO approval. Budget variance needs explanation.',
    updateDate: '18 Dec 2025',
    escalationDate: '15 Dec 2025',
    escalationReason: 'Budget overrun - needs immediate attention',
    escalatedTo: 'CEO',
    escalatedBy: 'Mike Chen',
    source: 'Finance Meeting - Dec 2025',
    sourceType: 'meeting',
    tags: ['Finance', 'Urgent'],
    refNumber: '892341',
    status: 'pending',
  },
  {
    id: 'esc-3',
    type: 'project',
    title: 'Digital Transformation Phase 2',
    assignee: 'Emily Davis',
    subAssignee: 'Alex Turner',
    sourceBadge: 'IT',
    summary: 'Infrastructure upgrade blocked by vendor delays. Alternative solutions being evaluated.',
    updateDate: '22 Dec 2025',
    escalationDate: '21 Dec 2025',
    escalationReason: 'Vendor dependency - risk to timeline',
    escalatedTo: 'Chairman',
    escalatedBy: 'Sarah Johnson',
    source: 'Project: Digital Transformation',
    sourceType: 'project',
    tags: ['IT', 'Blocked'],
    refNumber: '445521',
    status: 'pending',
  },
  {
    id: 'esc-4',
    type: 'action-item',
    title: 'Legal Contract Review - Partnership Agreement',
    assignee: 'David Brown',
    subAssignee: null,
    sourceBadge: 'Legal',
    summary: 'Partnership terms require board approval before signing. Deadline approaching.',
    updateDate: '19 Dec 2025',
    escalationDate: '17 Dec 2025',
    escalationReason: 'Time-sensitive legal matter',
    escalatedTo: 'CEO',
    escalatedBy: 'David Brown',
    source: 'CEO List',
    sourceType: 'list',
    tags: ['Legal', 'Critical'],
    refNumber: '667788',
    status: 'pending',
  },
  {
    id: 'esc-5',
    type: 'project',
    title: 'Customer Portal Enhancement',
    assignee: 'Mohamed Deyab',
    subAssignee: 'Team Alpha',
    sourceBadge: 'Product',
    summary: 'My escalation - Resource allocation needed for sprint completion.',
    updateDate: '20 Dec 2025',
    escalationDate: '19 Dec 2025',
    escalationReason: 'Resource constraint',
    escalatedTo: 'CEO',
    escalatedBy: 'Mohamed Deyab',
    source: 'Project: Customer Portal',
    sourceType: 'project',
    tags: ['Product', 'Resources'],
    refNumber: '334455',
    status: 'pending',
  },
];

// Filter options for escalations
const escalationFilters = [
  { id: 'chairman', label: 'Chairman', color: 'from-red-600 to-red-700' },
  { id: 'ceo', label: 'CEO', color: 'from-violet-600 to-purple-700' },
  { id: 'myself', label: 'Myself', color: 'from-blue-600 to-cyan-700' },
  { id: 'by-me', label: 'By Me', color: 'from-emerald-600 to-green-700' },
];

const MGMTApp = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  // Section visibility settings
  const [visibleSections, setVisibleSections] = useState({
    escalations: true,
    chairman: true,
    ceo: true,
    myself: true,
    'by-me': true,
  });
  
  // Collapsible states
  const [escalationsExpanded, setEscalationsExpanded] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/mgmt-login');
  };

  // Filter escalations based on selected filter
  const getFilteredEscalations = (filterId) => {
    switch(filterId) {
      case 'chairman':
        return mockEscalations.filter(e => e.escalatedTo === 'Chairman');
      case 'ceo':
        return mockEscalations.filter(e => e.escalatedTo === 'CEO');
      case 'myself':
        return mockEscalations.filter(e => e.assignee === 'Mohamed Deyab');
      case 'by-me':
        return mockEscalations.filter(e => e.escalatedBy === 'Mohamed Deyab');
      default:
        return mockEscalations;
    }
  };

  // Get count for each filter
  const getFilterCount = (filterId) => getFilteredEscalations(filterId).length;

  // Total escalations
  const totalEscalations = mockEscalations.length;

  // Theme
  const theme = {
    bg: darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50',
    card: darkMode ? 'bg-[#12121a]' : 'bg-white',
    cardHover: darkMode ? 'hover:bg-[#1a1a24]' : 'hover:bg-slate-50',
    text: darkMode ? 'text-white' : 'text-slate-900',
    textMuted: darkMode ? 'text-slate-400' : 'text-slate-500',
    border: darkMode ? 'border-white/5' : 'border-slate-200',
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} transition-all duration-500`}>
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-[100px]" />
        <div className="absolute -bottom-40 -left-40 w-60 h-60 bg-fuchsia-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-40 ${theme.card}/90 backdrop-blur-xl border-b ${theme.border}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => setDrawerOpen(true)}
            className="p-2 rounded-xl hover:bg-white/5 transition-colors"
            data-testid="pwa-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* MGMT Stack Logo */}
          <div className="flex items-center gap-3">
            <img src="/mgmt-logo.png" alt="MGMT Stack" className="w-9 h-9" />
            <div className="h-8 w-px bg-white/20" />
            <div className="flex flex-col leading-none">
              <span className="text-base font-bold bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                MGMT
              </span>
              <span className="text-base font-bold text-white/80">
                Stack
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-xl hover:bg-white/5 transition-colors"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button className="p-2 rounded-xl hover:bg-white/5 transition-colors relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-fuchsia-500 rounded-full" />
            </button>
          </div>
        </div>
      </header>

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Drawer */}
      <div className={`fixed top-0 left-0 h-full w-72 ${theme.card} z-50 transform transition-transform duration-300 ease-out ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 h-full flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src="/mgmt-logo.png" alt="MGMT Stack" className="w-10 h-10" />
              <div>
                <p className="font-bold">MGMT Stack</p>
                <p className={`text-xs ${theme.textMuted}`}>{user?.name || 'User'}</p>
              </div>
            </div>
            <button onClick={() => setDrawerOpen(false)} className="p-1 rounded-lg hover:bg-white/5">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Edit Button - Opens customization */}
          <button
            onClick={() => setEditModalOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600/20 to-fuchsia-600/20 border border-violet-500/30 mb-4 hover:from-violet-600/30 hover:to-fuchsia-600/30 transition-colors"
          >
            <Settings className="w-5 h-5 text-violet-400" />
            <span className="font-medium">Edit Dashboard</span>
          </button>

          <nav className="flex-1 space-y-1">
            {[
              { icon: AlertTriangle, label: 'Escalations', color: 'text-red-400' },
              { icon: Briefcase, label: 'Action Items', color: 'text-blue-400' },
              { icon: FileText, label: 'Projects', color: 'text-violet-400' },
              { icon: Calendar, label: 'Meetings', color: 'text-emerald-400' },
            ].map((item) => (
              <button
                key={item.label}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl ${theme.cardHover} transition-colors`}
              >
                <item.icon className={`w-5 h-5 ${item.color}`} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <button
            onClick={handleLogout}
            className="w-full py-3 rounded-xl bg-red-500/10 text-red-400 font-medium hover:bg-red-500/20 transition-colors mt-4"
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditModalOpen(false)} />
          <div className={`relative w-full max-w-sm ${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}>
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h3 className="font-semibold">Customize Dashboard</h3>
              <button onClick={() => setEditModalOpen(false)} className="p-1 rounded-lg hover:bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className={`text-sm ${theme.textMuted} mb-4`}>Select which sections to show:</p>
              
              {/* Section toggles */}
              {[
                { id: 'escalations', label: 'Escalations Section' },
                { id: 'chairman', label: 'Chairman Escalations' },
                { id: 'ceo', label: 'CEO Escalations' },
                { id: 'myself', label: 'Assigned to Me' },
                { id: 'by-me', label: 'Escalated by Me' },
              ].map((section) => (
                <label 
                  key={section.id}
                  className={`flex items-center gap-3 p-3 rounded-xl ${theme.cardHover} cursor-pointer transition-colors`}
                >
                  <div 
                    onClick={() => setVisibleSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                    className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                      visibleSections[section.id] 
                        ? 'bg-violet-600 border-violet-600' 
                        : `border-white/20 ${theme.cardHover}`
                    }`}
                  >
                    {visibleSections[section.id] && <Check className="w-4 h-4 text-white" />}
                  </div>
                  <span>{section.label}</span>
                </label>
              ))}
            </div>
            <div className="p-4 border-t border-white/5">
              <button 
                onClick={() => setEditModalOpen(false)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="relative z-10 p-4 pb-24 space-y-4">
        
        {/* Escalations Section */}
        {visibleSections.escalations && (
          <section>
            {/* Escalations Header Bar */}
            <button
              onClick={() => setEscalationsExpanded(!escalationsExpanded)}
              className="w-full bg-gradient-to-r from-red-600/90 to-red-700/90 rounded-2xl p-4 flex items-center gap-4 backdrop-blur-sm border border-red-500/20 shadow-lg shadow-red-500/10"
            >
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-200" />
              </div>
              <div className="flex-1 text-left">
                <h2 className="text-xl font-bold text-white">{totalEscalations} Escalations</h2>
                <p className="text-red-200/80 text-sm">Require immediate attention</p>
              </div>
              {escalationsExpanded ? (
                <ChevronUp className="w-6 h-6 text-red-200" />
              ) : (
                <ChevronDown className="w-6 h-6 text-red-200" />
              )}
            </button>

            {/* Expanded Content - Filter Boxes */}
            {escalationsExpanded && (
              <div className="mt-3 space-y-4 animate-in slide-in-from-top duration-300">
                {/* Filter Boxes Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {escalationFilters.filter(f => visibleSections[f.id]).map((filter) => (
                    <button
                      key={filter.id}
                      onClick={() => setSelectedFilter(selectedFilter === filter.id ? null : filter.id)}
                      className={`rounded-2xl p-4 text-center transition-all ${
                        selectedFilter === filter.id
                          ? `bg-gradient-to-br ${filter.color} shadow-lg scale-[1.02]`
                          : `${theme.card} border ${theme.border} hover:border-white/10`
                      }`}
                    >
                      <div className={`text-3xl font-bold ${selectedFilter === filter.id ? 'text-white' : ''}`}>
                        {getFilterCount(filter.id)}
                      </div>
                      <div className={`text-sm mt-1 ${selectedFilter === filter.id ? 'text-white/80' : theme.textMuted}`}>
                        {filter.label}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Escalation Cards */}
                {selectedFilter && (
                  <div className="space-y-3 mt-4">
                    {getFilteredEscalations(selectedFilter).map((escalation) => (
                      <EscalationCard 
                        key={escalation.id} 
                        escalation={escalation} 
                        theme={theme}
                        darkMode={darkMode}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

      </main>
    </div>
  );
};

// Escalation Card Component
const EscalationCard = ({ escalation, theme, darkMode }) => {
  return (
    <div className={`${theme.card} rounded-2xl border ${theme.border} overflow-hidden`}>
      {/* Type indicator bar */}
      <div className={`h-1 ${escalation.type === 'project' ? 'bg-blue-500' : 'bg-violet-500'}`} />
      
      {/* Title Header */}
      <div className={`p-4 ${escalation.type === 'project' ? 'bg-gradient-to-r from-red-700 to-red-800' : 'bg-gradient-to-r from-violet-700 to-purple-800'}`}>
        <div className="flex items-start gap-2">
          <div className="w-1 h-full bg-white/30 rounded-full self-stretch min-h-[40px]" />
          <h3 className="text-white font-semibold text-sm leading-tight flex-1">
            {escalation.title}
          </h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Assignee Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className={`w-4 h-4 ${theme.textMuted}`} />
            <span className="font-medium">{escalation.assignee}</span>
            {escalation.subAssignee && (
              <span className={`text-sm ${theme.textMuted}`}>& {escalation.subAssignee}</span>
            )}
          </div>
          <div className="w-10 h-10 rounded-full bg-slate-500 flex items-center justify-center">
            <Check className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Source Badge */}
        <div>
          <span className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-medium">
            {escalation.sourceBadge}
          </span>
        </div>

        {/* Summary Quote */}
        <div className={`border-l-4 border-violet-500 pl-4 py-2 ${darkMode ? 'bg-white/5' : 'bg-slate-50'} rounded-r-xl`}>
          <p className={`text-sm ${theme.textMuted}`}>{escalation.summary}</p>
        </div>

        {/* Date */}
        <div className={`flex items-center gap-2 text-sm ${theme.textMuted}`}>
          <Calendar className="w-4 h-4" />
          <span className="text-red-400">{escalation.updateDate}</span>
        </div>

        {/* Escalation Details */}
        <div className={`text-xs ${theme.textMuted} space-y-1`}>
          <p><span className="text-red-400">Escalated:</span> {escalation.escalationDate} • To: <span className="text-cyan-400">{escalation.escalatedTo}</span></p>
          <p><span className="text-amber-400">Reason:</span> {escalation.escalationReason}</p>
          <p><span className="text-violet-400">Source:</span> {escalation.source}</p>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 flex-wrap">
          {escalation.tags.map((tag, i) => (
            <span 
              key={i}
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                tag === 'Late' || tag === 'Urgent' || tag === 'Critical' 
                  ? 'bg-red-500/20 text-red-400' 
                  : tag === 'Blocked'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-slate-500/20 text-slate-400'
              }`}
            >
              {tag}
            </span>
          ))}
          <span className={`ml-auto text-xs ${theme.textMuted}`}>#{escalation.refNumber}</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <button className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium flex items-center justify-center gap-2">
            <Edit className="w-4 h-4" />
          </button>
          <button className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-medium flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4" />
          </button>
          <button className="flex-1 py-2.5 rounded-xl bg-fuchsia-600 text-white text-sm font-medium flex items-center justify-center gap-2">
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        {/* Request Update Button */}
        <button className="w-full py-3 rounded-xl bg-teal-600 text-white font-medium flex items-center justify-center gap-2 hover:bg-teal-500 transition-colors">
          <FileText className="w-5 h-5" />
          REQUEST AN UPDATE
        </button>
      </div>
    </div>
  );
};

export default MGMTApp;
